import { addMetadata, useCollections } from '@hb/react'
import {
  AnyObject,
  AppName,
  Collection,
  CollectionItem,
  CombinedCollectionEntry,
  DropResult,
  FieldMapValue,
  getBaseCollection,
  getCollectionId,
  reorder,
  UpdateCallback,
} from '@hb/shared'
import {
  CollectionReference,
  doc,
  collection as firestoreCollection,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore'
import cloneDeep from 'lodash.clonedeep'
import { db } from '../db'

export const getCollectionRef = <Data extends AnyObject>(
  collection: Collection<Data> | CombinedCollectionEntry<Data>,
): CollectionReference<Data> => {
  const baseCollection = getBaseCollection(collection)
  if (!baseCollection) throw new Error('No collection')
  return firestoreCollection(db, baseCollection.refPath) as CollectionReference<Data>
}

export const addItem = async <Data extends AnyObject>(
  appName: AppName,
  collection: Collection<Data>,
  data: FieldMapValue,
): Promise<string> => {
  // setLoading(true)
  const baseCollection = getBaseCollection(collection)
  if (!baseCollection) throw new Error('No collection')
  const { noRanks } = baseCollection
  const ref = getCollectionRef(collection)
  if (data.name) {
    try {
      const existsRes = await getDocs(query(ref, where('name', '==', data.name)))
      if (existsRes.empty) {
        const submitted = addMetadata(data, appName, true)
        if (!noRanks) {
          const items = await getDocs(ref)
          submitted.rank = items?.docs?.length ?? 0
        }
        const newDoc = doc(ref)
        await setDoc(newDoc, submitted as AnyObject)
        return newDoc.id
      }
      throw new Error('Name exists')
    } catch (err: any) {
      console.error({ baseCollection })
      throw new Error(`Error adding item: ${err.message}`)
    }
  } else {
    throw new Error('No name')
  }
}

export const reorderCollection = async <Data extends AnyObject>(
  collection: Collection<Data>,
  dropResult: DropResult,
): Promise<UpdateCallback> => {
  const baseCollection = getBaseCollection(collection)
  const collectionId = getCollectionId(collection)
  if (!baseCollection) throw new Error('No collection')
  const items = useCollections.getState()[collectionId]?.items as CollectionItem<Data>[]

  if (items && dropResult.source && dropResult.destination) {
    const old = cloneDeep(items)
    const result = reorder<CollectionItem<Data>>(old, dropResult)
    const batch = writeBatch(db)
    result.forEach((res, i) => {
      const ref = getCollectionRef(collection)
      batch.update(doc(ref, res.id), { rank: i })
    })
    return batch
      .commit()
      .catch(err => {
        console.error(err)
        throw new Error(`Error reordering items: ${err.message}`)
      })
      .then(() => ({ success: 'reordered successfully!' }))
  }
  return { error: 'no need to reorder' }
}
